<template>
  <div>
    <Header>
      勤怠変更差額確認
    </Header>
    <ApplicationCard
      v-if="application"
      :application="application"
      class="mt-3 mx-3 mb-2"
    />

    <CustomValidationObserver v-slot="{ handleSubmit }">
      <div class="p-3">
        <div class="pb-3">
          <p class="text-size-14 mt-1">
            規定の給与と変更後の給与の差額を確認し、勤怠変更を申請してください。
          </p>
        </div>

        <div class="bg-white border border-outline-color p-3 round-4">
          <div class="d-flex justify-content-between">
            <p class="text-mild text-size-14">勤務時間</p>
            <p class="text-mild text-size-14">
              {{ actualWorkStartAt }}-{{ actualWorkEndAt }}
            </p>
          </div>
          <div class="d-flex justify-content-between mt-1">
            <p class="text-mild text-size-14">休憩時間</p>
            <p
              v-if="actualRestStartAt && actualRestEndAt"
              class="text-mild text-size-14"
            >
              {{ actualRestStartAt }}-{{ actualRestEndAt }}
            </p>
            <p v-else class="text-mild text-size-14">
              休憩無し
            </p>
          </div>
        </div>

        <div class="bg-white border border-outline-color p-3 mt-3 round-4">
          <div class="d-flex justify-content-between">
            <p class="text-mild text-size-14">規定の給与</p>
            <p class="text-mild text-size-14">
              ¥
              {{ formatNumber(salaryPerDay) }}
            </p>
          </div>
          <div class="d-flex justify-content-between mt-1">
            <p class="text-mild text-size-14">交通費</p>
            <p class="text-mild text-size-14">
              ¥ {{ formatNumber(variousAllowances) }}
            </p>
          </div>
          <div class="d-flex justify-content-between mt-1">
            <p class="text-mild text-size-14 text-weight-500">規定額合計</p>
            <p class="text-mild text-size-14 text-weight-500">
              ¥ {{ formatNumber(salaryPerDay + variousAllowances) }}
            </p>
          </div>
          <div
            class="d-flex justify-content-between border-top border-outline-color mt-2"
          >
            <p class="text-mild text-size-14 mt-2">変更後の給与</p>
            <p class="text-mild text-size-14 mt-2">
              ¥ {{ formatNumber(actualSalaryPerDay) }}
            </p>
          </div>
          <div class="d-flex justify-content-between mt-1">
            <p class="text-mild text-size-14">交通費</p>
            <p class="text-mild text-size-14">
              ¥ {{ formatNumber(variousAllowances) }}
            </p>
          </div>
          <div class="d-flex justify-content-between mt-1">
            <p class="text-mild text-size-14 text-weight-500">変更申請額合計</p>
            <p class="text-mild text-size-14 text-weight-500">
              ¥
              {{ formatNumber(actualSalaryPerDay + variousAllowances) }}
            </p>
          </div>
          <div
            class="d-flex justify-content-between border-top border-outline-color mt-2"
          >
            <p class="text-size-16 text-weight-500 mt-2">差額</p>
            <p class="text-blue text-size-16 text-weight-500 mt-2">
              ¥ {{ formatNumber(differenceSalaryPerDay) }}
            </p>
          </div>
          <div class="mt-3">
            <p class="text-mild text-size-12">
              報酬額が9300円以上(交通費除く)の場合、別途源泉所得税が控除されます
            </p>
          </div>
        </div>
        <div class="mt-3">
          <ValidationProvider
            v-slot="validationState"
            name="コメント"
            :rules="{ required: true, max: 2000 }"
            vid="comment"
          >
            <TextAreaGroup
              v-model="comment"
              label="コメント"
              appLabelText="必須"
              placeholder="出勤時間から遅れてしまいましたので勤怠修正お願いします。"
              :validationState="validationState"
              class="mt-1"
            />
          </ValidationProvider>
        </div>
      </div>

      <div class="text-center">
        <AppCheckbox
          v-model="isCheckedAmount"
          id="contacted"
          text="報酬の差額を確認しました"
          class="cancel-check-box"
        />
      </div>

      <div class="p-3 mt-2">
        <AppButton
          variant="secondary"
          :block="true"
          :disabled="!comment || !isCheckedAmount"
          @click="handleSubmit(onClickSubmitButton)"
          >勤怠変更申請を提出する</AppButton
        >
        <AppButtonOutline
          @click="onClickBack"
          :block="true"
          class="d-block mt-3"
        >
          入力画面に戻る
        </AppButtonOutline>
      </div>
    </CustomValidationObserver>
  </div>
</template>

<script lang="ts">
import { Component, Vue } from "vue-facing-decorator";
import Header from "@/components/organisms/common/Header.vue";
import AppButton from "@/components/atoms/button/AppButton.vue";
import ApplicationCard from "@/components/organisms/applications/ApplicationCard.vue";
import AppButtonOutline from "@/components/atoms/button/AppButtonOutline.vue";
import AppCheckbox from "@/components/atoms/input/AppCheckbox.vue";
import TextAreaGroup from "@/components/molecules/form/TextAreaGroup.vue";
import { applicationService } from "@/app/services/ApplicationService";
import { orderService } from "@/app/services/OrderService";
import { ErrorType, showErrorModal } from "@/utils/showErrorModal";
import { applicationStore } from "@/store/application";
import { Application } from "@/types/order/Application";
import { formatNumber } from "@/utils/number";
import { attendanceCorrectionStore } from "@/store/attendance-correction";
import { formatFromISOString } from "@/utils/date";

@Component({
  components: {
    Header,
    AppButton,
    ApplicationCard,
    AppCheckbox,
    AppButtonOutline,
    TextAreaGroup
  }
})
export default class AttendanceCorrection extends Vue {
  application: Application | null = null;
  isCheckedAmount = false;
  comment = "";

  get applicationId() {
    return Number(this.$route.params.applicationId);
  }

  get actualWorkStartAt() {
    return this.salary?.actualStartWorkAt
      ? formatFromISOString(this.salary.actualStartWorkAt, "HH:mm")
      : "";
  }

  get actualWorkEndAt() {
    return this.salary?.actualFinishWorkAt
      ? formatFromISOString(this.salary.actualFinishWorkAt, "HH:mm")
      : "";
  }

  get actualRestStartAt() {
    return this.salary?.actualStartRestAt
      ? formatFromISOString(this.salary.actualStartRestAt, "HH:mm")
      : "";
  }

  get actualRestEndAt() {
    return this.salary?.actualFinishRestAt
      ? formatFromISOString(this.salary.actualFinishRestAt, "HH:mm")
      : "";
  }

  get salaryPerDay() {
    return this.application ? this.application.appliedSalaryPerDay : 0;
  }

  get variousAllowances() {
    return this.application ? this.application.variousAllowances : 0;
  }

  get actualSalaryPerDay() {
    return this.salary?.salary || 0;
  }

  get differenceSalaryPerDay() {
    return this.actualSalaryPerDay - this.salaryPerDay;
  }

  get salary() {
    return attendanceCorrectionStore.getSalary(this.applicationId);
  }

  async created() {
    this.$loading.show();
    try {
      await this.fetchApplication();
    } catch (error) {
      showErrorModal(this, error as ErrorType);
    } finally {
      this.$loading.hide();
    }
  }

  async fetchApplication() {
    this.application = await applicationService.fetchApplication(
      this.applicationId
    );
  }

  onClickBack() {
    this.$router.back();
  }

  async onClickSubmitButton() {
    this.$loading.show();
    try {
      const body = {
        actualStartedWorkAt: this.salary!.actualStartWorkAt!,
        actualFinishedWorkAt: this.salary!.actualFinishWorkAt!,
        actualStartedRestAt: this.salary!.actualStartRestAt || null,
        actualFinishedRestAt: this.salary!.actualFinishRestAt || null,
        workerComment: this.comment
      };

      this.application!.attendanceCorrection?.id
        ? await orderService.updateAttendanceCorrection(
            this.application!.attendanceCorrection.id,
            body
          )
        : await orderService.createAttendanceCorrection({
            application: this.applicationId,
            ...body
          });

      attendanceCorrectionStore.clearSalary(this.applicationId);
      await applicationStore.fetchApplications();
      await this.$router.push({
        name: "AttendanceCorrectionFinished",
        params: { applicationId: String(this.applicationId) }
      });
    } catch (error) {
      console.log(error);
      showErrorModal(this, error as ErrorType);
    } finally {
      this.$loading.hide();
    }
  }

  formatNumber(value: number) {
    return formatNumber(value);
  }
}
</script>

<style lang="scss" scoped>
.cancel-check-box {
  :deep(span) {
    font-size: 14px !important;
    vertical-align: middle;
  }
}
</style>
